import { type FC } from "react";

import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";

/**
 * WHAT:
 *   Formats duration in seconds to human-readable hours and minutes.
 *
 * WHY:
 *   Provides consistent duration display format.
 *
 * @param seconds - Duration in seconds (optional)
 *
 * @returns Formatted string (e.g., "2h 30m") or "N/A" if no duration
 *
 */
const formatDuration = (seconds?: number): string => {
  if (!seconds) {
    return "N/A";
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${String(hours)}h ${String(minutes)}m`;
  }
  return `${String(minutes)}m`;
};

interface StressCardProperties {
  className?: string;
  recoveryHigh?: number; // Sekunden
  stressHigh?: number; // Sekunden
}

/**
 * StressCard - Zeigt Stress- und Recovery-Zeiten
 *
 * Features:
 * - Verwendet globale Card-Komponente für konsistentes Styling
 * - Stress-Zeit in Minuten
 * - Recovery-Zeit in Minuten
 * - Stress-Ratio Bewertung (wie in StressDetail)
 * - Farbkodierung basierend auf Stress-Ratio
 *
 * @param root0
 *
 * @param root0.stressHigh
 *
 * @param root0.recoveryHigh
 *
 * @param root0.className
 *
 * @returns Stress card component
 *
 */
export const StressCard: FC<StressCardProperties> = ({
  className = "",
  recoveryHigh,
  stressHigh,
}) => {
  // Helper function for stress ratio rating (wie in StressDetail)
  const getStressRatioRating = (stressHigh: number, recoveryHigh: number) => {
    if (recoveryHigh === 0) {
      return { label: "No Recovery", variant: "destructive" as const };
    }

    const ratio = stressHigh / recoveryHigh;

    if (ratio < 2) {
      return { label: "Optimal", variant: "success" as const };
    }
    if (ratio <= 2.8) {
      return { label: "Good", variant: "info" as const };
    }
    if (ratio <= 3.4) {
      return { label: "Fair", variant: "warning" as const };
    }
    return { label: "Bad", variant: "destructive" as const };
  };

  const ratioRating =
    stressHigh && recoveryHigh
      ? getStressRatioRating(stressHigh, recoveryHigh)
      : { label: "N/A", variant: "default" as const };

  return (
    <Card className={className} padding="sm" variant="default">
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <h3 className="text-sm font-medium text-neutral-200">Stress</h3>
          <p className="text-xs text-neutral-400">Stress & Recovery</p>
        </div>
        <Badge className="text-xs" variant={ratioRating.variant}>
          {ratioRating.label}
        </Badge>
      </div>

      <div className="flex items-center justify-between mb-1.5">
        <div className="text-left">
          <div className="text-xs text-neutral-400">Recovery</div>
          <div className="text-lg font-bold text-neutral-100">{formatDuration(recoveryHigh)}</div>
        </div>

        <div className="text-right">
          <div className="text-xs text-neutral-400">Stress</div>
          <div className="text-lg font-bold text-neutral-100">{formatDuration(stressHigh)}</div>
        </div>
      </div>
    </Card>
  );
};
